/*  MUI  */
import { Autorenew, Cancel, CheckCircle, Event } from '@mui/icons-material';
import { Box, Chip } from '@mui/material';

/*  Shared  */
import { getDateTime } from 'application/Shared/Components/Core';
import { Currency } from 'application/Shared/Components/Text';
import { DarkTooltip } from 'application/Shared/Components/Tooltips';
import { FontSizes } from 'application/Shared/Constants';

/* NPM */
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

/*  React  */
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

dayjs.extend(advancedFormat);

const ContractContext = React.createContext({ contract: null });

export const Contract = ({ children, data }) => {
    const contractMemo = useMemo(() => ({ contract: data }), [data]);

    return (
        <ContractContext.Provider value={contractMemo}>
            {children}
        </ContractContext.Provider>
    );
};

export const ContractTypeIcon = ({ fontSize, sx }) => {
    const { t } = useTranslation();
    const { contract } = useContext(ContractContext);

    if (contract?.type === 'month') {
        return (
            <DarkTooltip arrow title={t('ContractManagement.monthToMonth')}>
                <Autorenew fontSize={fontSize} sx={{ ...sx }} />
            </DarkTooltip>
        );
    }
    if (contract?.type === 'renew') {
        return (
            <DarkTooltip arrow title={t('ContractManagement.renewalContract')}>
                <Event fontSize={fontSize} sx={{ ...sx }} />
            </DarkTooltip>
        );
    }
    return null;
};

export const ContractType = () => {
    const { t } = useTranslation();
    const { contract } = useContext(ContractContext);

    if (contract?.type === 'month') {
        return (
            <Chip
                data-testid="contractTypeChipMonth"
                sx={{
                    backgroundColor: 'contract.chipType.month.backgroundColor',
                    border: '1px solid',
                    borderColor: 'contract.chipType.month.borderColor'
                }}
                size="small"
                icon={
                    <ContractTypeIcon
                        sx={{
                            color: 'contract.chipType.month.iconColor',
                            fontSize: FontSizes[2],
                            marginLeft: '8px'
                        }}
                    />
                }
                label={t('ContractManagement.monthToMonthContract')}
            />
        );
    }
    if (contract?.type === 'renew') {
        return (
            <Chip
                data-testid="contractTypeChipRenew"
                sx={{
                    backgroundColor: 'contract.chipType.renew.backgroundColor',
                    border: '1px solid',
                    borderColor: 'contract.chipType.renew.borderColor'
                }}
                size="small"
                icon={
                    <ContractTypeIcon
                        sx={{
                            color: 'contract.chipType.renew.iconColor',
                            fontSize: FontSizes[2],
                            marginLeft: '8px'
                        }}
                    />
                }
                label={t('ContractManagement.renewalContract')}
            />
        );
    }
    return null;
};

export const ContractAlertingIconWithText = () => {
    const { t } = useTranslation();
    const { contract } = useContext(ContractContext);

    return contract?.alerting ? (
        <Box display="flex">
            <CheckCircle
                fontSize="small"
                sx={{ color: 'icon.success', marginRight: '2px' }}
            />
            {t('ContractManagement.alertSet')}
        </Box>
    ) : (
        <Box display="flex">
            <Cancel
                fontSize="small"
                sx={{ color: 'icon.error', marginRight: '2px' }}
            />
            {t('ContractManagement.alertNotSet')}
        </Box>
    );
};

export const ContractAlertingIcon = () => {
    const { contract } = useContext(ContractContext);

    return contract?.alerting ? (
        <CheckCircle fontSize="small" sx={{ color: 'icon.success' }} />
    ) : (
        <Cancel fontSize="small" sx={{ color: 'icon.error' }} />
    );
};

export const ContractTCV = () => {
    const { contract } = useContext(ContractContext);

    return <Currency currency={contract?.tcv} />;
};

export const ContractMRR = () => {
    const { contract } = useContext(ContractContext);

    return <Currency currency={contract?.mrr} />;
};

export const ContractNRC = () => {
    const { contract } = useContext(ContractContext);

    return <Currency currency={contract?.nrc} />;
};

export const ContractNotes = () => {
    const { contract } = useContext(ContractContext);
    return <span>{contract?.notes}</span>;
};

export const ContractUsers = ({ isForReport }) => {
    const { contract } = useContext(ContractContext);
    return (
        <span>{isForReport ? contract?.subscribedUsers : contract?.users}</span>
    );
};

export const ContractDate = ({ abbreviatedMonthDate }) => {
    const { t } = useTranslation();
    const { contract } = useContext(ContractContext);

    if (contract?.type === 'month') {
        // Convert day to ordinal
        return t('ContractManagement.monthlyOnTheDay', {
            day: dayjs(contract.renewalDate).format('Do')
        });
    }

    if (contract?.type === 'renew') {
        return getDateTime({
            abbreviatedMonthDate,
            date: contract?.renewalDate,
            noTimeString: true
        });
    }

    return <>--</>;
};
